/**
 * Authentication Guard for Report Summarizer
 * Handles authentication states and loading
 */

import { ReactNode } from 'react'
import { Container, Stack, Title, Text, Button, Loader, Paper } from '@mantine/core'
import { useAuth } from './AuthProvider'
import { shouldSkipAuth } from './config'

interface AuthGuardProps {
  children: ReactNode
}

/**
 * Loading component displayed while authentication is being checked
 */
function AuthLoading() {
  return (
    <Container size="sm" py="xl" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Paper withBorder shadow="sm" p="xl" style={{ width: '100%', textAlign: 'center' }}>
        <Stack gap="lg" align="center">
          <Loader size="lg" color="#635BFF" />
          <Stack gap="sm" align="center">
            <Title order={2} style={{ color: '#232A5C' }}>
              Loading...
            </Title>
            <Text size="sm" c="dimmed">
              Checking your authentication status
            </Text>
          </Stack>
        </Stack>
      </Paper>
    </Container>
  )
}

/**
 * Login component displayed when user is not authenticated
 */
function LoginRequired() {
  const { login } = useAuth()

  return (
    <Container size="sm" py="xl" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Paper withBorder shadow="sm" p="xl" style={{ width: '100%', textAlign: 'center' }}>
        <Stack gap="lg" align="center">
          <Stack gap="sm" align="center">
            <Title order={1} style={{ color: '#232A5C' }}>
              Summaries by <span style={{ color: '#635BFF' }}>Joy</span>
            </Title>
            <Text  size="lg" c="dimmed">
              Sign in to quickly turn your property documents into clear, actionable summaries.
            </Text>
          </Stack>
          
          <Button
            size="lg"
            onClick={login}
            style={{
              backgroundColor: '#635BFF',
              borderRadius: 999,
              paddingLeft: 32,
              paddingRight: 32,
            }}
          >
            Sign In with HappyCo
          </Button>
        </Stack>
      </Paper>
    </Container>
  )
}

/**
 * Error component displayed when authentication fails
 */
function AuthError({ error }: { error: string }) {
  const { login } = useAuth()

  return (
    <Container size="sm" py="xl" style={{ minHeight: '100vh', display: 'flex', alignItems: 'center' }}>
      <Paper withBorder shadow="sm" p="xl" style={{ width: '100%', textAlign: 'center' }}>
        <Stack gap="lg" align="center">
          <Stack gap="sm" align="center">
            <Title order={2} style={{ color: '#fa5252' }}>
              Authentication Error
            </Title>
            <Text size="sm" c="dimmed">
              {error}
            </Text>
          </Stack>
          
          <Button
            size="md"
            onClick={login}
            style={{
              backgroundColor: '#635BFF',
              borderRadius: 999,
            }}
          >
            Try Again
          </Button>
        </Stack>
      </Paper>
    </Container>
  )
}

/**
 * AuthGuard component that wraps the application and handles authentication states
 */
export function AuthGuard({ children }: AuthGuardProps) {
  // Skip authentication in devlocal environment
  if (shouldSkipAuth()) {
    return <>{children}</>
  }

  const { isAuthenticated, isLoading, error } = useAuth()

  if (isLoading) {
    return <AuthLoading />
  }

  if (error) {
    return <AuthError error={error} />
  }

  if (!isAuthenticated) {
    return <LoginRequired />
  }

  return <>{children}</>
}
